/**
 * Willkommensseite (`/welcome`) — öffentliche Marketing-/Einstiegsseite.
 *
 * Rein präsentativ: keine API-Aufrufe, kein Anmeldestatus, keine
 * Weiterleitung (Req 1.4, 1.5). Verwendet das zentrale App-Theme als globale
 * Basis und leitet daraus ein seitenlokales, verschachteltes Marketing-Theme
 * ab (dunkelblau/grün, Req 12.1/12.2). Gliederung in semantische Bereiche
 * header/main/footer mit durchgängiger Überschriften-Hierarchie (Req 11.4).
 */

import { useMemo } from "react";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { createWelcomeTheme, welcomeColors } from "./welcome/welcomeTheme";
import { useI18n } from "../i18n/I18nProvider";
import WelcomeHeader from "./welcome/WelcomeHeader";
import WelcomeHero from "./welcome/WelcomeHero";
import HowItWorks from "./welcome/HowItWorks";
import Benefits from "./welcome/Benefits";
import JoinCta from "./welcome/JoinCta";
import WelcomeFaq from "./welcome/WelcomeFaq";
import WelcomeFooter from "./welcome/WelcomeFooter";

/** Blauer Band-Streifen unter dem Hero (rein dekorativ, kein Feature-Versprechen). */
function HighlightStrip() {
  const { t } = useI18n();
  const theme = useTheme();
  const colors = welcomeColors(theme.palette.mode);

  const items = [
    { titleKey: "welcome.strip.a.title", textKey: "welcome.strip.a.text" },
    { titleKey: "welcome.strip.b.title", textKey: "welcome.strip.b.text" },
    { titleKey: "welcome.strip.c.title", textKey: "welcome.strip.c.text" },
  ];

  return (
    <Box
      sx={{
        bgcolor: colors.primarySurface,
        color: colors.onPrimary,
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography fontWeight={800} fontSize={18}>
              {t("welcome.strip.title")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {items.map((item) => (
                <Grid item xs={12} sm={4} key={item.titleKey}>
                  <Typography fontWeight={800}>{t(item.titleKey)}</Typography>
                  <Typography sx={{ opacity: 0.8, fontSize: 13 }}>
                    {t(item.textKey)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default function WelcomePage() {
  const outer = useTheme(); // zentrales App-Theme (hell/dunkel)
  const theme = useMemo(() => createWelcomeTheme(outer), [outer]);
  const colors = welcomeColors(outer.palette.mode);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: colors.pageBackground }}>
        <WelcomeHeader />

        <Box component="main" id="main">
          <WelcomeHero />
          <HighlightStrip />
          <HowItWorks />
          <Benefits />
          <JoinCta />
          <WelcomeFaq />
        </Box>

        <WelcomeFooter />
      </Box>
    </ThemeProvider>
  );
}
